import React, { FC, useState } from 'react';
import {
  Space,
  Button,
  Typography,
  List,
  Avatar,
  Modal,
  Menu,
  Spin,
  Empty,
  message,
  Input,
  Pagination,
} from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { DownOutlined, UserOutlined } from '@ant-design/icons';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import { useQuery } from 'react-query';

import {
  Drawer,
  Integration,
  ReferenceField,
  StatusField,
  TerminalId,
  TextField,
} from '~/components';
import { employees, terminals } from '~/routes';
import { Employee, Location, Terminal } from '~/types';
import { FlashOffIcon } from '~/img';
import { deactivateTerminal, getEmployeesByTerminal } from '~/api/terminals';
import { Longdash } from '~/utils';
import { getLocationById } from '~/api/locations';
import TerminalEdit from './TerminalEdit';
import { usePermissions } from '~/hooks';
import IssueActivationCode from './IssueActivationCode';
import TerminalBlock from './TerminalBlock';
import { usePageShow } from '~/hooks/usePageQuery';
import { useTerminalsEdit, useTerminalsQuery } from '~/hooks/terminals';
import { useAllLocations } from '~/hooks/locations';
import useIsLocationMandatory from '~/hooks/useIsLocationMandatory';
import DeviceIcon from '~/components/DeviceIcon';
import { AccountsByIds } from '~/components/fields/AccountById';

interface TerminalShowProps {
  className?: string;
}

const TerminalShow: FC<TerminalShowProps> = (props) => {
  const { className } = props;
  const { data, open, showClose } = usePageShow<Terminal>(terminals);
  const { data: locationsData, isLoading: locationsLoading } = useAllLocations();

  const isLocationMandatory = useIsLocationMandatory();
  const [t] = useTranslation('terminals');

  const [openLocations, setOpenLocations] = useState(false);
  const handleOpenLocations = () => setOpenLocations(true);
  const handleCloseLocations = () => setOpenLocations(false);

  const { mutate, isLoading } = useTerminalsEdit(handleCloseLocations);

  const [newLocationId, setNewLocationId] = useState<string | null>(null);
  const handleChangeLocation = () => {
    if (!data) return;
    const result: Terminal = { ...data };
    if (newLocationId && newLocationId !== 'null') {
      result.locationId = newLocationId;
    } else if (newLocationId === 'null') {
      delete result.locationId;
    }
    mutate({
      id: data.id as string,
      body: result,
    });
  };

  const [currentPage, setCurrentPage] = useState(1); // State for current page
  const [pageSize, setPageSize] = useState(10); // State for page size
  const [searchTerm, setSearchTerm] = useState(''); // State for search term

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value.toLowerCase());
    setCurrentPage(1); // Reset to first page on search
  };

  const filteredLocations = locationsData?.filter((item) =>
    item.name.toLowerCase().includes(searchTerm)
  );

  const paginatedLocations = filteredLocations?.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const hasAccessUpdate = usePermissions('business.terminal.update') && data?.status !== 'CLOSED';
  if (!data) return null;

  return (
    <>
      <Drawer
        className={className}
        title={data?.label}
        onClose={showClose}
        open={open}
        footer={
          data?.status !== 'CLOSED' && (
            <Space direction="horizontal" className="TerminalShow-BottomTooltip">
              {data?.status !== 'ACTIVE' && (
                <TerminalBlock id={data?.id as string} closeParent={showClose} />
              )}
              <TerminalEdit initialValues={data} />
            </Space>
          )
        }
      >
        <Space direction="vertical" size="large" style={fullWidth}>
          <Space direction="vertical" size="middle" style={fullWidth}>
            {data?.id && <TerminalId id={data?.id} withCopy />}
            <StatusField data={data?.status} />
            <TextField label={t('type')}>
              {data?.terminalType ? t(data?.terminalType) : Longdash}
            </TextField>
            <TextField label={t('linkedAccounts')}>
              {data?.accountIds ? (
                <AccountsByIds accountIds={data?.accountIds} withCurrency />
              ) : (
                Longdash
              )}
            </TextField>
            <TextField label={t('location')}>
              {hasAccessUpdate ? (
                <Typography.Link
                  className="TerminalShow-Location"
                  onClick={handleOpenLocations}
                  ellipsis
                >
                  {data?.locationId ? (
                    <>
                      <ReferenceField
                        selector={(location: Location) => location.name}
                        query={getLocationById(data.locationId)}
                        deps={[data.locationId]}
                      />
                      <DownOutlined />
                    </>
                  ) : (
                    <>
                      <div className="text-gray">{t('notDefined')}</div>
                      <DownOutlined />
                    </>
                  )}
                </Typography.Link>
              ) : data?.locationId ? (
                <>
                  <ReferenceField
                    selector={(location: Location) => location.name}
                    query={getLocationById(data.locationId)}
                    deps={[data.locationId]}
                  />
                </>
              ) : (
                <>
                  <div className="text-gray">{t('notDefined')}</div>
                </>
              )}
            </TextField>
            <TextField label={t('linkedPhone')}>
              {data?.linkedPhone ? <bdi>{data?.linkedPhone}</bdi> : t('noPhone')}
            </TextField>
          </Space>
          {data?.terminalType === 'MPOS' && (
            <DeviceCard record={data} device={data?.device} status={data?.status} />
          )}
          {data?.additionalProperties?.NFCTerminal && (
            <Space direction="vertical" size="middle" style={fullWidth}>
              <Typography.Title level={5} className="Terminal-Title5">
                {t('additionalData')}
              </Typography.Title>
              <TextField label={t('NFCTerminal')}>
                {data.additionalProperties?.NFCTerminal}
              </TextField>
            </Space>
          )}
          {data?.terminalType === 'MPOS' && <EmployessList terminal={data} />}
          {data?.terminalType === 'ECOMMERCE' && data?.id && <Integration terminalId={data?.id} />}
        </Space>
      </Drawer>
      <Modal
        title={t('changeTerminalLocation')}
        visible={openLocations}
        onOk={handleChangeLocation}
        okButtonProps={{ disabled: !newLocationId || newLocationId === data?.locationId }}
        confirmLoading={isLoading}
        onCancel={handleCloseLocations}
      >
        <Input.Search
          placeholder={t('searchLocation')}
          onChange={handleSearch}
          value={searchTerm}
          style={{ marginBottom: 16 }}
        />
        {locationsLoading && <Spin />}
        {isEmpty(data) && !locationsLoading && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        <Menu
          defaultSelectedKeys={[data?.locationId as string]}
          onSelect={({ key }) => setNewLocationId(key as string | null)}
        >
          {!isLocationMandatory && <Menu.Item key={null}>{Longdash}</Menu.Item>}
          {paginatedLocations?.map((item) => (
            <Menu.Item key={item.id}>{item.name}</Menu.Item>
          ))}
        </Menu>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={filteredLocations?.length || 0}
          onChange={(page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          }}
          style={{ marginTop: 16, textAlign: 'center' }}
        />
      </Modal>
    </>
  );
};

interface DeviceCardProps {
  record: Terminal;
  device: Terminal['device'] | undefined;
  status: Terminal['status'];
}

const DeviceCard: FC<DeviceCardProps> = (props) => {
  const { device, status, record } = props;
  const [t] = useTranslation('terminals');
  const { refetch } = useTerminalsQuery({ enabled: false });

  const [loading, setLoading] = useState(false);
  const handleInactivate = async () => {
    setLoading(true);
    try {
      await deactivateTerminal(record.id as string);
      void refetch();
    } catch (error: any) {
      const err: any = error;
      void message.error(err.message);
    }
    setLoading(false);
  };

  const hasAccessMakeInactive = usePermissions('business.terminal.make inactive');
  const hasAccessActivate = usePermissions('business.terminal.issue activation code');
  return (
    <Space className="DeviceCard" direction="vertical">
      {status === 'ACTIVE' ? (
        <>
          <div className="DeviceCard-Header">
            <span className="DeviceCard-Title">{t('device')}</span>
            {hasAccessMakeInactive && (
              <Button
                size="small"
                ghost
                danger
                icon={<FlashOffIcon />}
                onClick={handleInactivate}
                loading={loading}
              >
                {t('makeInactive')}
              </Button>
            )}
          </div>
          <div className="DeviceCard-Details">
            <Space direction="vertical">
              <TextField label={t('applicationId')}>{device?.applicationId}</TextField>
              <TextField label={t('deviceLabel')}>{device?.deviceLabel}</TextField>
              <TextField label={t('OS')} className="DeviceCard-Details-OS">{`${
                device?.osName || Longdash
              } ${device?.osVersion || ''}`}</TextField>
              <TextField label={t('enrolledAt')}>
                {device?.enrolledAt && moment(device?.enrolledAt).format('LL LT')}
              </TextField>
            </Space>
            <DeviceIcon
              osName={record?.device?.osName}
              active={record?.status === 'ACTIVE'}
              className="DeviceCard-Img"
              type={record?.terminalType}
            />
          </div>
        </>
      ) : status !== 'CLOSED' ? (
        <>
          <div>{t('noDevice')}</div>
          {hasAccessActivate && <IssueActivationCode record={record} />}
        </>
      ) : null}
    </Space>
  );
};

const EmployessList: FC<{ terminal: Terminal | null }> = (props) => {
  const { terminal } = props;

  const { data, isLoading } = useQuery(
    [employees, terminal?.id],
    () => getEmployeesByTerminal(terminal?.id as string).then((res) => res.data),
    { enabled: !!terminal }
  );

  const [t] = useTranslation('terminals');

  return (
    <div>
      <Typography.Title level={5}>{t('linkedEmployees')}</Typography.Title>
      <List<Employee>
        itemLayout="horizontal"
        dataSource={data}
        loading={isLoading}
        renderItem={(item) => {
          const { firstName, lastName, phoneNumber } = item;
          return (
            <List.Item>
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} />}
                title={<div>{`${firstName} ${lastName}`}</div>}
                description={<bdi>{phoneNumber}</bdi> || t('noPhone')}
              />
            </List.Item>
          );
        }}
      />
    </div>
  );
};

const fullWidth = { width: '100%' };

export default styled(TerminalShow)`
  .TerminalShow-BottomTooltip {
    display: flex;
    justify-content: flex-end;
  }

  .TerminalShow-Location {
    display: flex;
    align-items: center;

    .anticon {
      margin-left: 3px;
    }
  }

  .Terminal-Title5 {
    margin-bottom: 0;
  }
`;
